import {
  NotesContentWrapper,
  NotesDividerWrapper,
  NotesDivider,
  NotesWrapper,
  NotesInput,
  NotesTextArea,
  ButtonsWrapper,
  StyledButton,
  ContentWrapper,
} from './TicketDetailsNotesSection.styled';
import { StatusChangedNote } from '../Notes/StatusChangedNote';
import { NoteContent } from '../Notes/Note';
import { EmptyNotesBody } from '../EmptyNotesBody/EmptyNotesBody';
import { useState, useRef, useEffect } from 'react';
import { useGetNotes } from '../../../../hooks/useGetNotes';
import { Note } from '../../../../models/Note.interface';
import { FileAttachment } from '../FileAttachment/FileAttachment';
import { AttachPopover } from '../AttachFilePopover/AttachFilePopover';
import { SendRegular } from '@fluentui/react-icons';
import { useCreateNote } from '../../../../hooks/useCreateNote';
import { fileReader } from '../utils/FileReader';
import { ErrorMessage } from './ErrorMessage/ErrorMessage';
import { Alert } from 'src/components/common/AlertMessage/AlertMessage';

interface TicketDetailsNotesSectionProps {
  axsTicketId: string | number;
  ticketOwner: string;
}

export const TicketDetailsNotesSection = ({
  axsTicketId,
  ticketOwner,
}: TicketDetailsNotesSectionProps) => {
  const [showMessageBar, setShowMessageBar] = useState<boolean>(false);
  const { notesData, getNotesData } = useGetNotes(axsTicketId, showMessageBar);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<File>();
  const {
    createNote,
    hideAlert,
    requestStatusAppearance,
    requestStatusMessage,
  } = useCreateNote();
  const [noteText, setNoteText] = useState<string>('');
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const bottomElemRef = useRef(null);

  const compareNotes = (firstNote: Note, secondNote: Note) => {
    return Date.parse(firstNote.createdon) - Date.parse(secondNote.createdon);
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFileUploaded(false);
  };

  const handleUploadFromDevice = async () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.addEventListener('change', (event) => {
      const fileList = (event.target as HTMLInputElement).files;
      if (fileList && fileList.length > 0) {
        fileReader(fileList[0])
          .then((data) => {
            if (data.trim().length === 0) {
              setShowErrorMessage(true);
              setErrorMessage('Upload failed. File is empty');
            } else {
              setShowErrorMessage(false);
              setFileUploaded(true);
              setUploadedFile(fileList[0]);
            }
          })
          .catch((err) => {
            console.error(err);
            setShowErrorMessage(true);
            setErrorMessage('Upload failed. File is empty');
          });
      }
    });
    fileInput.click();
    fileInput.remove();
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteText(event.target.value);

    if (event.target.value.length > 4) {
      setShowErrorMessage(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (noteText.trim().length > 4 || uploadedFile) {
        await createNote(axsTicketId, noteText, uploadedFile);
        setNoteText('');
        setFileUploaded(false);
        setUploadedFile(null);
        setShowMessageBar(true);
        await getNotesData();
      } else {
        setShowErrorMessage(true);
        setErrorMessage('Note text should be at least 5 characters long.');
      }
    } catch (err) {
      console.error(err);
      setShowMessageBar(true);
    }
  };

  useEffect(() => {
    bottomElemRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [notesData]);

  return (
    <ContentWrapper>
      {requestStatusMessage && (
        <Alert
          requestStatusAppearance={requestStatusAppearance}
          requestStatusMessage={requestStatusMessage}
          showCloseButton={true}
          overlay={false}
          handleClose={hideAlert}
        />
      )}
      <NotesContentWrapper>
        {notesData &&
          notesData?.sort(compareNotes).map((currNote) => {
            if (
              currNote &&
              currNote?.notetext &&
              currNote?.notetext.startsWith('Status Reason changed to:')
            ) {
              const newStatus = currNote?.notetext.substring(
                'Status Reason changed to:'.length
              );
              return (
                <StatusChangedNote
                  key={currNote?.annotationid}
                  note={currNote}
                  newStatus={newStatus}
                  ticketOwner={ticketOwner}
                />
              );
            } else {
              return (
                <NoteContent key={currNote?.annotationid} note={currNote} />
              );
            }
          })}
        <div ref={bottomElemRef} />
        {notesData?.length === 0 && <EmptyNotesBody />}
      </NotesContentWrapper>

      <NotesDividerWrapper>
        {notesData?.length !== 0 && <NotesDivider />}
      </NotesDividerWrapper>

      <NotesWrapper>
        <NotesInput tabIndex={0}>
          <NotesTextArea
            placeholder="Enter note text here."
            value={noteText}
            onChange={handleTextChange}
          ></NotesTextArea>
          {fileUploaded && (
            <FileAttachment
              fileName={uploadedFile.name}
              onClose={handleRemoveFile}
            />
          )}
        </NotesInput>
        {showErrorMessage && <ErrorMessage message={errorMessage} />}

        <ButtonsWrapper>
          <AttachPopover handleUpload={handleUploadFromDevice} />
          <StyledButton
            size="medium"
            icon={<SendRegular />}
            onClick={handleSubmit}
          >
            Submit
          </StyledButton>
        </ButtonsWrapper>
      </NotesWrapper>
    </ContentWrapper>
  );
};
